import type { QueryClient } from '@tanstack/react-query';
import { redirect, type AnyRouter } from '@tanstack/react-router';
import type { Panel } from '../api';
import { sessionQuery, sessionQueryKey, type AuthDeps, type Principal, type Role } from './session';

// What every route carries (UI architecture §3).
export type RouteStaticData = { nav?: string; roles?: readonly Role[]; public?: boolean };

// A route with roles needs one of them; a route without roles, any principal (UI architecture §6).
export function requireRole(
  roles: readonly Role[] | undefined,
  principal: Principal | null,
): boolean {
  return principal !== null && (!roles?.length || roles.includes(principal.role));
}

// The root route's beforeLoad, over the staticData of the matched routes (UI architecture §6). A
// public route is open; any other needs a session; a Merchant with unfinished onboarding is sent
// to its current step (merchant screens §2 Redirects); a role outside a route's roles renders the
// inline "yetkiniz yok" state in place, with no redirect (merchant screens §1 rule 5; admin screens
// §1 rule 3).
export async function guardRoute({
  api,
  queryClient,
  location,
  staticData,
}: AuthDeps & {
  location: { pathname: string; href: string };
  staticData: ReadonlyArray<RouteStaticData | undefined>;
}): Promise<{ forbidden: boolean }> {
  if (staticData.some((data) => data?.public)) return { forbidden: false };
  const principal = await queryClient.ensureQueryData(sessionQuery(api));
  // <AnyRouter>: this package knows no app's routes, so the redirects must not be checked against
  // the router an app registers.
  if (principal === null) {
    throw redirect<AnyRouter>({ to: '/login', search: { returnTo: location.href } });
  }
  if (
    'merchant' in principal &&
    !principal.merchant.onboarding_complete &&
    !location.pathname.startsWith('/onboarding/')
  ) {
    throw redirect<AnyRouter>({ to: `/onboarding/${principal.merchant.onboarding_current_step}` });
  }
  return { forbidden: !staticData.every((data) => requireRole(data?.roles, principal)) };
}

// The API client's onUnauthenticated (UI architecture §6): the session is gone, so the session
// query holds null and the app goes to /login, keeping where it was. Already on /login it stays,
// so a second 401 of the same moment changes nothing.
export function redirectToLogin(
  router: Pick<AnyRouter, 'navigate' | 'state'>,
  queryClient: QueryClient,
  panel: Panel,
): void {
  queryClient.setQueryData(sessionQueryKey(panel), null);
  const { pathname, href } = router.state.location;
  if (pathname !== '/login') void router.navigate({ to: '/login', search: { returnTo: href } });
}
